import axios from 'axios';
import toast from 'react-hot-toast';

// Determine the API base URL based on environment
const getApiBaseUrl = () => {
  // Priority 1: Use REACT_APP_API_URL if explicitly set (for separate backend)
  if (process.env.REACT_APP_API_URL) {
    console.log('[API Config] Using API URL from REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // Priority 2: Check hostname at runtime (works even if build wasn't done with NODE_ENV=production)
  const hostname = window.location.hostname;
  console.log('[API Config] Current hostname:', hostname);
  
  if (hostname === 'sastamastaride.com' || hostname === 'www.sastamastaride.com') {
    const backendUrl = 'https://ridewithme-84eb.onrender.com';
    console.log('[API Config] Detected sastamastaride.com domain, using backend API URL:', backendUrl);
    return backendUrl;
  }
  
  // Priority 3: In production build, check NODE_ENV
  if (process.env.NODE_ENV === 'production') {
    // If we're here and not on sastamastaride.com, assume same domain
    console.log('[API Config] Production mode: Using relative API paths (same domain)');
    return '';
  }
  
  // Priority 4: Development - use relative path (proxy in package.json handles it)
  console.log('[API Config] Development mode: Using relative API paths (proxy configured)');
  return '';
};

const apiBaseUrl = getApiBaseUrl();
console.log('[API Config] Final baseURL:', apiBaseUrl || '(empty - relative paths)');

// Create axios instance with base URL
const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and log requests
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log the final URL being used for debugging
    const fullUrl = config.baseURL 
      ? `${config.baseURL}${config.url}` 
      : config.url;
    console.log('[API Request]', config.method?.toUpperCase(), fullUrl);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error logging for debugging
    if (error.response) {
      // Server responded with error status
      console.error('API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        data: error.response.data
      });
    } else if (error.request) {
      // Request was made but no response received
      console.error('API Error - No Response:', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        message: 'Network error or server not responding'
      });
    } else {
      // Error in request setup
      console.error('API Error - Request Setup:', error.message);
    }
    
    if (error.response?.status === 401) {
      // Unauthorized - but don't logout if it's a rate limit or temporary error
      // Only logout on actual authentication failures
      const url = error.config?.url || '';
      
      // Don't logout for rate limit related 401s or polling endpoints
      if (!url.includes('/unread-count') && !url.includes('/notifications')) {
        // Check if it's a token expiration or invalid token
        const errorMessage = error.response?.data?.message || '';
        if (errorMessage.includes('token') || errorMessage.includes('unauthorized') || errorMessage.includes('expired')) {
          // Only logout on actual auth failures, not rate limits
          if (!errorMessage.toLowerCase().includes('too many')) {
            localStorage.removeItem('token');
            // Use setTimeout to avoid navigation during render
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          }
        }
      }
    }
    
    // Handle rate limit errors (429) - don't treat as critical
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const message = error.response?.data?.message || 'Too many requests. Please wait a moment.';
      
      // Only show toast for non-polling endpoints to avoid spam
      const url = error.config?.url || '';
      if (!url.includes('/unread-count') && !url.includes('/notifications')) {
        toast.error(message, { duration: 3000 });
      }
      
      // Return a special error that can be handled by callers
      return Promise.reject({
        ...error,
        isRateLimit: true,
        retryAfter: retryAfter ? parseInt(retryAfter) * 1000 : null
      });
    }
    
    return Promise.reject(error);
  }
);

export default api;

